import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  alpha,
  useTheme
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  CalendarMonth as CalendarIcon,
  Percent as PercentIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon
} from '@mui/icons-material';

// Globals Editor - Production Variables (like Movie Magic Budgeting)
function BudgetGlobals({ budget, onSave }) {
  const theme = useTheme();
  
  const [globals, setGlobals] = useState({
    prepDays: 10,
    shootDays: 20,
    wrapDays: 5,
    holidayDays: 0,
    sixthDayRate: 1.5,
    seventhDayRate: 2.0,
    overtimeRate: 1.5,
    doubleTimeRate: 2.0,
    locationCity: '',
    locationState: '',
    unionStatus: 'non-union',
    productionType: 'feature'
  });

  const [fringes, setFringes] = useState({
    enabled: true,
    rates: {
      fica: 7.65,
      futa: 0.6,
      suta: 3.4,
      workersComp: 3.0,
      healthWelfare: 0,
      pension: 6.0,
      vacationHoliday: 8.33
    },
    caps: {
      futa: 7000,
      suta: 7000
    }
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (budget?.globals) {
      setGlobals(prev => ({ ...prev, ...budget.globals }));
    }
    if (budget?.fringes) {
      setFringes(prev => ({ 
        ...prev, 
        ...budget.fringes,
        rates: { ...prev.rates, ...budget.fringes?.rates },
        caps: { ...prev.caps, ...budget.fringes?.caps }
      }));
    }
  }, [budget]);

  const handleGlobalChange = (field) => (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value;
    setGlobals(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleFringeRateChange = (field) => (e) => {
    const value = parseFloat(e.target.value) || 0;
    setFringes(prev => ({
      ...prev,
      rates: { ...prev.rates, [field]: value }
    }));
    setHasChanges(true);
  };

  const handleFringeCapChange = (field) => (e) => {
    const value = parseFloat(e.target.value) || 0;
    setFringes(prev => ({
      ...prev,
      caps: { ...prev.caps, [field]: value }
    }));
    setHasChanges(true);
  };

  const handleFringeToggle = (e) => {
    setFringes(prev => ({ ...prev, enabled: e.target.checked }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave({ globals, fringes });
    setHasChanges(false);
  };

  const totalFringeRate = fringes.enabled ? 
    Object.values(fringes.rates).reduce((sum, rate) => sum + rate, 0) : 0;

  return (
    <Box>
      {/* Save Button */}
      {hasChanges && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Box>
      )}

      {/* Production Schedule */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Production Schedule
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Prep Days"
                type="number"
                value={globals.prepDays}
                onChange={handleGlobalChange('prepDays')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Shoot Days"
                type="number"
                value={globals.shootDays}
                onChange={handleGlobalChange('shootDays')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Wrap Days"
                type="number"
                value={globals.wrapDays}
                onChange={handleGlobalChange('wrapDays')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Holiday Days"
                type="number"
                value={globals.holidayDays}
                onChange={handleGlobalChange('holidayDays')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                <Typography variant="body2" color="text.secondary">
                  Total Production Days: <strong>{globals.prepDays + globals.shootDays + globals.wrapDays + globals.holidayDays}</strong>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Overtime Rates */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PercentIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Overtime & Premium Rates
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="6th Day Rate"
                type="number"
                value={globals.sixthDayRate}
                onChange={handleGlobalChange('sixthDayRate')}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">×</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="7th Day Rate"
                type="number"
                value={globals.seventhDayRate}
                onChange={handleGlobalChange('seventhDayRate')}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">×</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Overtime Rate"
                type="number"
                value={globals.overtimeRate}
                onChange={handleGlobalChange('overtimeRate')}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">×</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Double Time Rate"
                type="number"
                value={globals.doubleTimeRate}
                onChange={handleGlobalChange('doubleTimeRate')}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">×</InputAdornment>
                }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Production Info */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Production Information
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Production Type"
                value={globals.productionType}
                onChange={handleGlobalChange('productionType')}
                select
                fullWidth
                size="small"
              >
                <MenuItem value="feature">Feature Film</MenuItem>
                <MenuItem value="tv-movie">TV Movie</MenuItem>
                <MenuItem value="tv-series">TV Series</MenuItem>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="short">Short Film</MenuItem>
                <MenuItem value="documentary">Documentary</MenuItem>
                <MenuItem value="music-video">Music Video</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Union Status"
                value={globals.unionStatus}
                onChange={handleGlobalChange('unionStatus')}
                select
                fullWidth
                size="small"
              >
                <MenuItem value="union">Union (SAG/DGA/IATSE)</MenuItem>
                <MenuItem value="non-union">Non-Union</MenuItem>
                <MenuItem value="mixed">Mixed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Location City"
                value={globals.locationCity}
                onChange={handleGlobalChange('locationCity')}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Location State"
                value={globals.locationState}
                onChange={handleGlobalChange('locationState')}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Fringes & Payroll */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <SettingsIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Fringes & Payroll Taxes
            </Typography>
            <Box sx={{ ml: 'auto', mr: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total: {totalFringeRate.toFixed(2)}%
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch 
                checked={fringes.enabled} 
                onChange={handleFringeToggle}
                color="primary"
              />
            }
            label="Enable Automatic Fringe Calculations"
            sx={{ mb: 2 }}
          />

          {fringes.enabled && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                PAYROLL TAX RATES
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="FICA (SS/Medicare)"
                    type="number"
                    value={fringes.rates.fica}
                    onChange={handleFringeRateChange('fica')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="FUTA"
                    type="number"
                    value={fringes.rates.futa}
                    onChange={handleFringeRateChange('futa')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="SUTA"
                    type="number"
                    value={fringes.rates.suta}
                    onChange={handleFringeRateChange('suta')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Workers Comp"
                    type="number"
                    value={fringes.rates.workersComp}
                    onChange={handleFringeRateChange('workersComp')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Pension/401k"
                    type="number"
                    value={fringes.rates.pension}
                    onChange={handleFringeRateChange('pension')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Vacation/Holiday"
                    type="number"
                    value={fringes.rates.vacationHoliday}
                    onChange={handleFringeRateChange('vacationHoliday')}
                    fullWidth
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                WAGE CAPS
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="FUTA Cap"
                    type="number"
                    value={fringes.caps.futa}
                    onChange={handleFringeCapChange('futa')}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="SUTA Cap"
                    type="number"
                    value={fringes.caps.suta}
                    onChange={handleFringeCapChange('suta')}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                  />
                </Grid>
              </Grid>

              <Paper sx={{ p: 2, mt: 3, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                <Typography variant="body2">
                  <strong>Total Fringe Rate:</strong> {totalFringeRate.toFixed(2)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This rate is automatically applied to labor costs in Above The Line and Below The Line - Production categories.
                </Typography>
              </Paper>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

export default BudgetGlobals;
